// Dependencies
import { useState, useEffect, useRef, useCallback, useMemo, useContext } from "react";
import { ReactSVG } from "react-svg";
import _, { has } from "lodash";

// Types
import { Types, Components } from '../types';

// Utils
import { getAssetURL } from '../utils/helpers'

// Hooks
import { useDebug } from "../hooks";

// Contexts
//import AppContext from "../contexts/AppContext";

// Components 
import Price from "./Price";
import { QuantitySelect } from "./Product/QuantitySelect";
import Overlay from "./Overlay";
import { MinicartContext } from "../contexts/Minicart";
import styled, { css } from "styled-components";
import Icon from "./Icon";

// const CartItemWrapper = styled.div<any>(({}) => [])
const CartItemImage = styled.img<any>(({ theme: { general: { transition, ...general }, components: { cartItem: { image: styles } } } }) => css`
    width: clamp(3em, 3vw, 4em);
    height: clamp(3em, 3vw, 4em);
    //object-fit: cover;
    object-fit: contain;
    object-position: center;
    border-radius: ${styles.borderRadius}em;
    overflow: hidden;
    grid-area: 1 / 1 / span 2 / 1;
    mix-blend-mode: multiply; 
    background-color: #fff;
`)
const CartItemName = styled.a<any>(({ theme: { general: { transition, ...general }, components: { cartItem: { name: styles } } } }) => css`
    text-decoration: none;
    color: ${styles.textColor};
    font-weight: 500;
    font-size: .85em;
    line-height: 1.25;
`)

const CartItemRemove = styled.div<any>(({ theme: { general: { transition, ...general }, components: { cartItem: { remove: styles } } } }) => css`
    opacity: 0.5; 
    cursor: pointer;
    grid-area: 1 / 4;
    align-self: start;
    justify-self: end;
    border-radius: 50%;
    transition: all ${transition.duration} ${transition.timingFunction};
    transition-duration: 100ms;
    -webkit-tap-highlight-color: transparent;
    line-height: 1;

    > div {
        width: 1em;
        height: 1em;
    }

    &:active {
        background-color: #1d1d1b10;
        box-shadow: 0 0 0 .5em #1d1d1b10;
    }
`)

const CartItemSubtotal = styled.div<any>(({ theme: { general: { transition, ...general }, components: { cartItem: { subtotal: styles } } } }) => css`
    font-weight: 500;
    grid-area: 2 / 3 / 2 / span 2;
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    .wooptima-cart-item--subtotal {
        &__per-unit {
            font-size: 0.75em;
            color: ${styles.pricePerUnit.textColor};
            &::after {
                content: ' / ks';
            }
            margin-bottom: 0.25em;
        }
    
        &__subtotal {
            color: ${styles.subtotal.textColor};
            font-size: 1.15em;
            line-height: 1;
        }
    }
`)

const CartItemAttributes = styled.div<any>(({ theme: { general: { transition, ...general }, components: { cartItem: { attributes: styles } } } }) => css`
    font-size: 0.9em;
    margin-top: .25em;
    color: ${styles.textColor};
`)

const CartItemMeta = styled.div<any>(({ theme: { general: { transition, ...general }, components: { cartItem: { meta: styles } } } }) => css`
    display: flex;
    flex-direction: column;
    grid-area: 1 / 2 / auto / span 3;
    padding-right: 1.5em;
`)


const CartItemWrapper = styled.div<any>(({ theme: { general: { transition, ...general }, components: { cartItem: styles } } }) => css`

    width: 100%;
    transition: background-color ${transition.duration} ${transition.timingFunction};
    transition-duration: 250ms;
    position: relative;
    
    &:not(.bundled-item) {
        display: grid;
        grid-template-columns: clamp(3em, 3vw, 4em) 1fr 1fr 3fr;  
        padding: clamp(1em, 5vw, 1.35em) clamp(1.5em, 5vw, 2em);
        background: ${styles.background.default};
        grid-column-gap: 1em;
        grid-row-gap: 0.75em;

        @media (hover: hover) {
            &:hover {
                & {
                    background: ${styles.background.hover}
                }
                .wooptima-cart-item__remove {
                    opacity: 0.5;
                }
            } 
            .wooptima-cart-item__remove {
                opacity: 0.25; 
                &:hover {
                    opacity: .75;
                } 
            }
        }
    }
    
    &.bundled-item {
        display: flex;
        align-items: center;
        padding: 0.8em clamp(1.5em, 5vw, 2em);
        padding-left: clamp(2.5em, 5vw, 3em);

        ${CartItemImage}  {
            width: 2em;
            height: 2em;
            margin-right: 0.85em;
        }

        ${CartItemAttributes} {
            margin-top: .1em;
        }

    }

`)

const CartItem = ({ cartItemID, productID, image, name, isUpdating, quantity, subtotal, attributes, type, price, pricePerUnit, className, bundledBy, permalink, meta, ...props }: any) => {
	useDebug('CartItem');

	const { loading, opened, items, itemsCount, itemsSubtotal, toggleState, config, translations, theme, handleItemRemove, handleItemUpdate } = useContext(MinicartContext);

	// Helpers
	const getFormattedAttributesList = () => Object.values(attributes).join(', '); // 

	return (
		<CartItemWrapper className={_.isEmpty(bundledBy) ? type : 'bundled-item'}>
			<CartItemImage src={image.feature} alt={name} className={`${className}__image`} />
			<CartItemMeta className={`${className}__meta`}>

				<CartItemName href={permalink} target="_blank" rel="noreferrer" className={`${className}__name`}>{name}</CartItemName>
				{(type === 'variation' && !_.isEmpty(attributes)) && <CartItemAttributes className={`${className}__attributes`}>{getFormattedAttributesList()}</CartItemAttributes>}
			</CartItemMeta>
			{
				_.isEmpty(bundledBy) && (<>
					<CartItemRemove
						as={Icon}
						size={1.25}
						tabIndex={0}
						role="button"
						color="#000"
						className={`${className}__remove`}
						id="cross"
						onClick={() => handleItemRemove({ cartItemID })}
					/>
					{!has(meta, 'wa_cart_gift') && (
						<QuantitySelect
							quantity={quantity}
							onChange={(newQuantity: number) => handleItemUpdate({ name: 'quantity', value: newQuantity, cartItemID })}
						/>
					)}
					<CartItemSubtotal className={`${className}--subtotal`}>
						{!has(meta, 'wa_cart_gift') && (
							<Price value={pricePerUnit} className={[`${className}--subtotal__per-unit`]}
								{...{ loading }}
								{...{ config, translations, theme }}
							/>
						)}
						{/* <QuantitySelect className={`${className}`} min={1} max={99} quantity={quantity} onChange={handleQuantityUpdate} /> */} 
						<Price value={! has(meta, 'wa_cart_gift') ? subtotal : 0} className={[`${className}--subtotal__subtotal`]}  {...{ loading }} {...{ config, translations, theme }} />
					</CartItemSubtotal>
				</>)
			}
		</CartItemWrapper>
	)
}

export default CartItem; 